import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { adminAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { hasBillingRole } from '../../../utils/roles';
import { formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import InventoryStylePagination from '../../../components/InventoryStylePagination';

// RF14: the ops queue for billing exceptions. Rows are raised
// automatically when billing work cannot complete (storage capture
// failed during a receive, a customer rate is missing for performed
// work). Ops resolves or dismisses each with mandatory notes.
const TYPE_LABELS = {
  storage_capture_failed: 'Storage Capture Failed',
  missing_rate: 'Missing Rate',
  unconfigured_class_rate: 'Unconfigured Class Rate',
  outbound_billing_input_missing: 'Outbound Billing Input Missing',
  completed_request_uninvoiced: 'Completed Request Uninvoiced',
  zoho_sync_failed: 'Zoho Sync Failed',
  correction_billing_review: 'Correction Billing Review'
};

const STATUS_STYLES = {
  open: 'bg-red-100 text-red-800',
  resolved: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-600'
};

const BillingExceptions = () => {
  const { user } = useAuth();
  // Exception updates are role-gated on the backend (RF15).
  const canMutateBilling = hasBillingRole(user);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState('open');
  const [typeFilter, setTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const { data, isLoading, isError, error, refetch } = useQuery(
    ['admin-billing-exceptions', page, limit, statusFilter, typeFilter],
    () => adminAPI.getBillingExceptions({
      page,
      limit,
      status: statusFilter || undefined,
      type: typeFilter || undefined
    }),
    { keepPreviousData: true }
  );

  // Internal users for the assignee picker (RF14). The endpoint only
  // returns internal staff; active-only keeps departed users out of the
  // dropdown while existing assignments still render via Assignee.
  const { data: usersData } = useQuery(
    ['admin-internal-users-for-exceptions'],
    () => adminAPI.getUsers({ status: 'active', limit: 200 }),
    { staleTime: 5 * 60 * 1000, enabled: canMutateBilling }
  );
  const internalUsers = usersData?.data || [];

  const exceptions = data?.exceptions || [];
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / limit));

  const transition = async (exception, status) => {
    let resolutionNotes;
    if (status === 'resolved' || status === 'dismissed') {
      resolutionNotes = window.prompt(
        status === 'resolved'
          ? 'Resolution notes (required) -- what was done to fix this?'
          : 'Dismissal notes (required) -- why does this need no action?'
      );
      if (resolutionNotes === null) return;
      if (!resolutionNotes.trim()) {
        toast.error('Notes are required');
        return;
      }
    }
    try {
      await adminAPI.updateBillingException(exception.id, {
        status,
        resolution_notes: resolutionNotes
      });
      toast.success(status === 'open' ? 'Exception reopened' : `Exception ${status}`);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update exception');
    }
  };

  const setAssignee = async (exception, assignedTo) => {
    try {
      await adminAPI.updateBillingException(exception.id, { assigned_to: assignedTo });
      toast.success(assignedTo ? 'Assignee updated' : 'Unassigned');
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update assignee');
    }
  };

  if (isError) {
    return <ErrorAlert message={error?.message || 'Failed to load billing exceptions'} />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Billing Exceptions</h1>
          <p className="mt-1 text-sm text-gray-500">
            Work the system could not bill automatically. Every open exception is a potential revenue gap.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="dismissed">Dismissed</option>
          <option value="">All statuses</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          className="border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : exceptions.length === 0 ? (
        <p className="text-gray-500 text-center py-12 bg-white rounded-lg border border-gray-200">
          No {statusFilter || ''} billing exceptions
        </p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Raised</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Warehouse</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Assignee</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {exceptions.map((exception) => (
                <React.Fragment key={exception.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === exception.id ? null : exception.id)}
                  >
                    <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(exception.created_at)}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-900">{TYPE_LABELS[exception.exception_type] || exception.exception_type}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-900">{exception.Company?.name || '-'}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{exception.Warehouse?.name || '-'}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[exception.status] || ''}`}>
                        {exception.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {exception.status === 'open' && canMutateBilling ? (
                        <select
                          value={exception.assigned_to || ''}
                          onChange={(e) => setAssignee(exception, e.target.value || null)}
                          className="border border-gray-300 rounded-md py-1 px-2 text-xs max-w-[170px] focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Unassigned</option>
                          {/* Keep a departed/inactive assignee selectable-as-current */}
                          {exception.assigned_to && !internalUsers.some((u) => u.id === exception.assigned_to) && (
                            <option value={exception.assigned_to}>
                              {exception.Assignee?.name || 'Assigned user'}
                            </option>
                          )}
                          {internalUsers.map((internalUser) => (
                            <option key={internalUser.id} value={internalUser.id}>
                              {internalUser.name}{internalUser.id === user?.id ? ' (me)' : ''}
                            </option>
                          ))}
                        </select>
                      ) : exception.Assignee ? (
                        <span className="text-gray-900">{exception.Assignee.name}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {!canMutateBilling ? (
                        <span className="text-xs text-gray-400">View only</span>
                      ) : exception.status === 'open' ? (
                        <>
                          <button
                            onClick={() => transition(exception, 'resolved')}
                            className="text-sm text-green-700 hover:text-green-900 font-medium mr-3"
                          >
                            Resolve
                          </button>
                          <button
                            onClick={() => transition(exception, 'dismissed')}
                            className="text-sm text-gray-500 hover:text-gray-700"
                          >
                            Dismiss
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => transition(exception, 'open')}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Reopen
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedId === exception.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={7} className="px-4 py-3 text-sm text-gray-700">
                        <p className="mb-2">{exception.summary}</p>
                        {exception.details && (
                          <pre className="text-xs text-gray-500 bg-white border border-gray-200 rounded p-2 overflow-x-auto">
                            {JSON.stringify(exception.details, null, 2)}
                          </pre>
                        )}
                        {exception.resolution_notes && (
                          <p className="mt-2 text-xs text-gray-500">
                            <span className="font-medium">Resolution:</span> {exception.resolution_notes}
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <InventoryStylePagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={data?.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
        />
      </div>
    </div>
  );
};

export default BillingExceptions;
